import { defineStore } from 'pinia';
import axios from 'axios';
import {getAPIURL} from '../logic/api';

const initialState = {
  scenarios: [],
  currentScenario: null,
  profile: null,
  loading: false,
  error: null
};

export const useScenariosStore = defineStore('scenarios', {
  state: () => ({ ...initialState }),

  getters: {
    // Get all scenarios
    allScenarios: (state) => state.scenarios || [],
    
    // Get the default scenario
    defaultScenario: (state) => {
      if (!state.scenarios || !state.scenarios.length) {
        return null;
      }
      return state.scenarios.find(scenario => 
        scenario.name && scenario.name.toLowerCase() === 'default'
      );
    },
    
    // Get current scenario
    selectedScenario: (state) => state.currentScenario,
    
    // Check if current scenario is the default
    isDefaultScenario: (state) => {
      return state.currentScenario && 
             state.currentScenario.name && 
             state.currentScenario.name.toLowerCase() === 'default';
    },
    
    // Get scenarios excluding the default
    customScenarios: (state) => {
      if (!state.scenarios || !state.scenarios.length) {
        return [];
      }
      return state.scenarios.filter(scenario => 
        scenario.name && scenario.name.toLowerCase() !== 'default'
      );
    },
    
    // Check if scenarios are loaded
    hasScenarios: (state) => state.scenarios && state.scenarios.length > 0,
    
    // Get loading state
    isLoading: (state) => state.loading,
    
    // Get error state
    hasError: (state) => state.error !== null
  },

  actions: {
    // Set the profile for this store
    setProfile(profile) {
      this.profile = profile;
    },

    // Fetch all scenarios for the current profile
    async fetchScenarios() {
      if (!this.profile?.id) {
        console.error('No profile set for fetching scenarios');
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        // console.log('Fetching scenarios for profile:', this.profile.id);
        
        const url = getAPIURL()+'/api/scenario/get-all-scenarios-for-profile';
        const response = await axios.get(url, {
          params: { profileID: this.profile.id }
        });
        
        this.scenarios = response.data;
        // console.log('Fetched scenarios:', this.scenarios);
        
        // Auto-select default scenario if available
        if (this.scenarios.length > 0) {
          const defaultScenario = this.defaultScenario;
          // console.log('Default scenario found:', defaultScenario);
          if (defaultScenario) {
            // console.log('Selecting default scenario:', defaultScenario);
            await this.selectScenario(defaultScenario);
          } else {
            // If no default scenario, select the first one
            // console.log('No default scenario, selecting first scenario:', this.scenarios[0]);
            await this.selectScenario(this.scenarios[0]);
          }
        } else {
          // console.log('No scenarios found');
        }
      } catch (error) {
        console.error('Error fetching scenarios:', error);
        this.error = error.message || 'Failed to fetch scenarios';
      } finally {
        this.loading = false;
      }
    },

    // Select a scenario
    async selectScenario(scenario) {
      if (!scenario) {
        console.error('selectScenario: scenario is null or undefined');
        return;
      }

      // console.log('Selecting scenario:', scenario);
      this.currentScenario = scenario;
      
      // Emit an event that other stores can listen to
      this.$patch((state) => {
        state.currentScenario = scenario;
      });
    },

    // Select scenario by ID
    async selectScenarioById(scenarioId) {
      const scenario = this.scenarios.find(s => s.id === scenarioId);
      if (scenario) {
        await this.selectScenario(scenario);
      } else {
        console.error('Scenario not found with ID:', scenarioId);
      }
    },

    // Select the default scenario
    async selectDefaultScenario() {
      const defaultScenario = this.defaultScenario;
      if (defaultScenario) {
        await this.selectScenario(defaultScenario);
      } else {
        console.warn('No default scenario found');
      }
    },

    // Create a new scenario
    async createScenario(name, description, is_default = false) {
      if (!this.profile?.id) {
        throw new Error('No profile set for creating scenario');
      }

      this.loading = true;
      this.error = null;

      try {
        // console.log('Creating scenario:', { name, description, is_default });
        
        const url = getAPIURL()+'/api/scenario/create-scenario';
        const response = await axios.post(url, {
          profileID: this.profile.id,
          name: name,
          description: description,
          is_default: is_default
        });
        
        // console.log('Created scenario:', response.data);
        
        // Refresh scenarios after creation
        await this.fetchScenarios();
        
        return response.data;
      } catch (error) {
        console.error('Error creating scenario:', error);
        this.error = error.message || 'Failed to create scenario';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Delete a scenario
    async deleteScenario(scenarioId) {
      if (!this.profile?.id) {
        throw new Error('No profile set for deleting scenario');
      }

      this.loading = true;
      this.error = null;

      try {
        // console.log('Deleting scenario:', scenarioId);
        
        const url = getAPIURL()+'/api/scenario/remove-scenario';
        await axios.delete(url, {
          data: {
            profileID: this.profile.id,
            scenarioID: scenarioId
          }
        });
        
        // console.log('Scenario deleted successfully');
        
        // If we deleted the current scenario, select the default
        if (this.currentScenario?.id === scenarioId) {
          await this.selectDefaultScenario();
        }
        
        // Refresh scenarios after deletion
        await this.fetchScenarios();
      } catch (error) {
        console.error('Error deleting scenario:', error);
        this.error = error.message || 'Failed to delete scenario';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Update a scenario
    async updateScenario(scenarioId, updates) {
      if (!this.profile?.id) {
        throw new Error('No profile set for updating scenario');
      }

      this.loading = true;
      this.error = null;

      try {
        // console.log('Updating scenario:', scenarioId, updates);
        
        const url = getAPIURL()+'/api/scenario/update-scenario';
        const response = await axios.put(url, {
          profileID: this.profile.id,
          scenarioID: scenarioId,
          ...updates
        });
        
        // console.log('Updated scenario:', response.data);
        
        // Refresh scenarios after update
        await this.fetchScenarios();
        
        return response.data;
      } catch (error) {
        console.error('Error updating scenario:', error);
        this.error = error.message || 'Failed to update scenario';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Reset the store to initial state
    reset() {
      Object.assign(this, initialState);
    },

    // Reset for new user
    resetForNewUser() {
      this.reset();
      this.profile = null;
    },

    // Clear error
    clearError() {
      this.error = null;
    }
  }
});
